import React from "react"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Trophy, BookOpen, Star, TrendingUp, Target, Award } from "lucide-react"
import { Input } from "@/components/ui/input"
import Image from "@/components/ui/image"
import { MathText } from "./math-text"
import type { QuizSession, Question } from "../types/quiz"
import { SignupPrompt } from "./signup-prompt"
import { useAuth } from "../contexts/auth-context"
import { useTranslation } from "../contexts/translation-context"

interface QuizInterfaceProps {
  session: QuizSession
  onAnswer: (answer: string[]) => void
  onNext: () => void
  onBack: () => void
  resultsSaved?: boolean // Add this to track if results were saved
}

export function QuizInterface({ session, onAnswer, onNext, onBack, resultsSaved = false }: QuizInterfaceProps) {
  const { t } = useTranslation()
  const [textAnswers, setTextAnswers] = useState<string[]>([])
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const { isAuthenticated } = useAuth()

  const currentQuestion = session.questions[session.currentQuestionIndex]
  const progress = ((session.currentQuestionIndex + 1) / session.questions.length) * 100
  const hasAnswered = session.answers[session.currentQuestionIndex] !== null
  const selectedAnswer = session.answers[session.currentQuestionIndex]

  // Initialize text answers array based on blanks count
  useEffect(() => {
    if (currentQuestion.type === "fill-in-blank") {
      const count = getBlankCount(currentQuestion)
      setTextAnswers(new Array(count).fill(""))
    }
    setSelectedOptions([])
  }, [session.currentQuestionIndex, currentQuestion])

  const handleMultipleChoiceSelection = (optionKey: string) => {
    if (hasAnswered) return

    if (isMultipleAnswersQuestion(currentQuestion)) {
      // Multiple selection allowed
      setSelectedOptions((prev) => {
        if (prev.includes(optionKey)) {
          return prev.filter((key) => key !== optionKey)
        } else {
          return [...prev, optionKey]
        }
      })
    } else {
      // Single selection - automatically submit
      onAnswer([optionKey])
    }
  }

  const handleSubmitMultipleChoice = () => {
    if (selectedOptions.length > 0) {
      onAnswer(selectedOptions)
    }
  }

  const handleTextAnswerChange = (index: number, value: string) => {
    setTextAnswers((prev) => {
      const newAnswers = [...prev]
      newAnswers[index] = value
      return newAnswers
    })
  }

  const handleSubmitTextAnswers = () => {
    const trimmedAnswers = textAnswers.map((answer) => answer.trim()).filter((answer) => answer !== "")
    if (trimmedAnswers.length === textAnswers.length) {
      onAnswer(trimmedAnswers)
      setTextAnswers(new Array(getBlankCount(currentQuestion)).fill(""))
    }
  }

  const handleSignupSuccess = () => {
    console.log("User authenticated, quiz results will be handled by the main quiz logic")
  }

  // Helper function to check if answers are correct
  const areAnswersCorrect = (userAnswers: string[], correctAnswers: string[]) => {
    if (currentQuestion.type === "multiple-choice") {
      // For multiple choice, check if arrays have same elements (order doesn't matter)
      if (userAnswers.length !== correctAnswers.length) return false
      return userAnswers.every((answer) => correctAnswers.includes(answer))
    } else {
      // For fill-in-blank, check exact match in order (case insensitive)
      if (userAnswers.length !== correctAnswers.length) return false
      return userAnswers.every((answer, index) => answer.toLowerCase() === correctAnswers[index].toLowerCase())
    }
  }

  // Helper function to get button styling for multiple choice options
  const getOptionButtonStyle = (optionKey: string) => {
    if (!hasAnswered) {
      // Before answering - show selection state with consistent text color
      if (selectedOptions.includes(optionKey)) {
        return "bg-blue-100 text-black border-blue-200 hover:bg-blue-150 hover:text-black"
      }
      return "bg-white text-black border-gray-200 hover:bg-blue-50 hover:text-black"
    }

    // After answering - show correct/incorrect feedback with consistent text color
    const isCorrectAnswer = currentQuestion.correctAnswers.includes(optionKey)
    const wasSelected = selectedAnswer?.includes(optionKey)

    if (isCorrectAnswer) {
      // Correct answers are always highlighted in green
      return "bg-green-100 text-black border-green-200 hover:bg-green-100 hover:text-black"
    } else if (wasSelected) {
      // Incorrect selected answers are highlighted in red
      return "bg-red-100 text-black border-red-200 hover:bg-red-100 hover:text-black"
    }

    // Unselected incorrect answers remain neutral
    return "bg-gray-50 text-black border-gray-200 hover:bg-gray-50 hover:text-black"
  }

  // Simplified helper function to determine if a multiple choice question allows multiple answers
  const isMultipleAnswersQuestion = (question: Question): boolean => {
    return question.type === "multiple-choice" && question.correctAnswers.length > 1
  }

  // Simplified helper function to determine the number of blanks for fill-in-blank questions
  const getBlankCount = (question: Question): number => {
    return question.correctAnswers.length
  }

  if (session.isComplete) {
    const correctAnswers = session.answers.filter((answer, i) => {
      const q = session.questions[i]
      if (!answer) return false
      return areAnswersCorrect(answer, q.correctAnswers)
    }).length

    const totalPossiblePoints = session.questions.reduce((sum, q) => sum + q.points, 0)
    const correctPercentage = Math.round((correctAnswers / session.questions.length) * 100)

    // Get performance level and styling
    const getPerformanceLevel = (percentage: number) => {
      if (percentage >= 90)
        return { level: t("results.excellent"), color: "text-green-600", bgColor: "bg-green-50", icon: Award }
      if (percentage >= 80)
        return { level: t("results.greatJob"), color: "text-primary", bgColor: "bg-primary-50", icon: Trophy }
      if (percentage >= 70)
        return { level: t("results.goodWork"), color: "text-secondary", bgColor: "bg-secondary-50", icon: Star }
      if (percentage >= 60)
        return { level: t("results.keepGoing"), color: "text-tertiary", bgColor: "bg-tertiary-50", icon: Target }
      return { level: t("results.keepPracticing"), color: "text-gray-600", bgColor: "bg-gray-50", icon: Target }
    }

    const performance = getPerformanceLevel(correctPercentage)
    const PerformanceIcon = performance.icon

    // For unauthenticated users completing random quiz, show integrated signup prompt
    if (!isAuthenticated && session.mode === "random") {
      return (
        <div className="max-w-2xl mx-auto p-4">
          <Card className="overflow-hidden">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-br from-primary to-primary-600 text-white p-6 text-center">
              <div className="mx-auto w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4">
                <PerformanceIcon className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-3xl font-bold mb-2">{performance.level}</CardTitle>
              <CardDescription className="text-primary-100 text-lg">{t("results.quizCompleted")}</CardDescription>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Score Display */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`${performance.bgColor} rounded-xl p-4 text-center`}>
                  <div className={`text-3xl font-bold ${performance.color} mb-1`}>{session.score}</div>
                  <div className="text-sm text-muted-foreground">{t("results.pointsEarned")}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("results.outOf")} {totalPossiblePoints}
                  </div>
                </div>
                <div className="bg-muted rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-primary mb-1">{correctPercentage}%</div>
                  <div className="text-sm text-muted-foreground">{t("results.accuracy")}</div>
                  <div className="text-xs text-muted-foreground">
                    {correctAnswers}/{session.questions.length} correct
                  </div>
                </div>
              </div>

              {/* Performance Breakdown */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg text-center">{t("results.yourPerformance")}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">{t("results.questionsAnswered")}</span>
                    <Badge variant="outline">{session.questions.length}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">{t("results.correctAnswers")}</span>
                    <Badge className="bg-green-100 text-green-800 border-green-200">{correctAnswers}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Points per Question</span>
                    <Badge variant="outline">{Math.round(session.score / session.questions.length)}</Badge>
                  </div>
                </div>
              </div>

              {/* Benefits Section */}
              <div className="bg-gradient-to-r from-secondary-50 to-primary-50 rounded-xl p-5 space-y-4">
                <h4 className="font-semibold text-lg text-center text-primary">{t("results.signUpToUnlock")}</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center gap-3 bg-white/60 rounded-lg p-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{t("results.signUpBenefits.saveProgress")}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("results.signUpBenefits.saveProgressDescription")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/60 rounded-lg p-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <BookOpen className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{t("results.signUpBenefits.accessExams")}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("results.signUpBenefits.accessExamsDescription")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/60 rounded-lg p-3">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{t("results.signUpBenefits.analytics")}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("results.signUpBenefits.analyticsDescription")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <SignupPrompt
                  score={session.score}
                  totalQuestions={session.questions.length}
                  onClose={onBack}
                  onSignupSuccess={handleSignupSuccess}
                  embedded={true}
                />
                <Button onClick={onBack} variant="outline" className="w-full">
                  {t("results.maybeLater")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    // For authenticated users or exam mode, show enhanced completion screen
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Card className="overflow-hidden">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-br from-primary to-primary-600 text-white p-6 text-center">
            <div className="mx-auto w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4">
              <PerformanceIcon className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold mb-2">{performance.level}</CardTitle>
            <CardDescription className="text-primary-100 text-lg">
              {session.mode === "exam" ? t("results.examCompleted") : t("results.quizCompleted")}
            </CardDescription>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Score Display */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-tertiary-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-tertiary mb-1">{session.score}</div>
                <div className="text-xs text-muted-foreground">{t("quiz.points")}</div>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-primary mb-1">{correctPercentage}%</div>
                <div className="text-xs text-muted-foreground">{t("results.accuracy")}</div>
              </div>
              <div className="bg-muted rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-tertiary mb-1">{correctAnswers}</div>
                <div className="text-xs text-muted-foreground">{t("results.correct")}</div>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="bg-muted rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">{t("results.performanceSummary")}</span>
                <Badge className={`${performance.bgColor} ${performance.color} border-0`}>
                  {performance.level.replace("!", "")}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{t("results.questionsAnswered")}</span>
                  <span className="font-medium">{session.questions.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("results.correctAnswers")}</span>
                  <span className="font-medium text-green-600">{correctAnswers}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("results.incorrectAnswers")}</span>
                  <span className="font-medium text-red-600">{session.questions.length - correctAnswers}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("results.averagePointsPerQuestion")}</span>
                  <span className="font-medium">{Math.round(session.score / session.questions.length)}</span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button onClick={onBack} className="w-full bg-primary hover:bg-primary-600 text-white py-3">
              {t("quiz.backToDashboard")}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="mb-4">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("common.back")}
        </Button>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            {t("quiz.question")} {session.currentQuestionIndex + 1} {t("quiz.of")} {session.questions.length}
          </span>
          <Badge variant="outline" className="bg-tertiary-50 text-tertiary border-tertiary-200">
            <Trophy className="w-3 h-3 mr-1" />
            {session.score} {t("quiz.points")}
          </Badge>
        </div>
        <Progress value={progress} className="h-2 bg-blue-100" indicatorClassName="bg-primary" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg leading-relaxed">
            <MathText>{currentQuestion.question}</MathText>
          </CardTitle>
          {isMultipleAnswersQuestion(currentQuestion) && (
            <CardDescription className="text-blue-600 font-medium">{t("quiz.selectAllCorrect")}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Question Image */}
          {currentQuestion.image && (
            <div className="flex justify-center">
              <div className="relative max-w-full">
                <Image
                  src={currentQuestion.image || "/placeholder.svg"}
                  alt={currentQuestion.imageAlt || "Question diagram"}
                  width={350}
                  height={250}
                  className="rounded-lg border border-gray-200 max-w-full h-auto"
                  priority
                />
              </div>
            </div>
          )}

          {currentQuestion.type === "multiple-choice" && currentQuestion.options && (
            <>
              {Object.entries(currentQuestion.options).map(([key, option]) => (
                <div key={key}>
                  <Button
                    variant="outline"
                    className={`w-full text-left justify-start h-auto p-4 whitespace-normal transition-all duration-200 ${getOptionButtonStyle(key)}`}
                    onClick={() => handleMultipleChoiceSelection(key)}
                    disabled={hasAnswered}
                  >
                    <div className="flex items-center gap-3 w-full">
                      {isMultipleAnswersQuestion(currentQuestion) && (
                        <div
                          className={`w-5 h-5 flex-shrink-0 rounded border ${selectedOptions.includes(key) && !hasAnswered
                              ? "bg-blue-500 border-blue-500"
                              : "border-gray-300"
                            } flex items-center justify-center`}
                        >
                          {selectedOptions.includes(key) && !hasAnswered && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-white"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className="mr-3 font-semibold">{key.toUpperCase()}.</span>
                        <MathText inline>{option}</MathText>
                      </div>
                    </div>
                  </Button>
                </div>
              ))}

              {isMultipleAnswersQuestion(currentQuestion) && !hasAnswered && (
                <Button
                  onClick={handleSubmitMultipleChoice}
                  className="w-full bg-primary hover:bg-primary-600 mt-4"
                  disabled={selectedOptions.length === 0}
                >
                  {selectedOptions.length > 1 ? t("quiz.submitAnswers") : t("quiz.submitAnswer")}
                </Button>
              )}
            </>
          )}

          {currentQuestion.type === "fill-in-blank" && (
            <div className="space-y-4">
              {Array.from({ length: getBlankCount(currentQuestion) }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <label className="text-sm font-medium">
                    {getBlankCount(currentQuestion) > 1 ? `${t("quiz.answer")} ${index + 1}:` : t("quiz.yourAnswer")}
                  </label>
                  <Input
                    type="text"
                    placeholder={`${t("quiz.typeAnswerHere")} ${getBlankCount(currentQuestion) > 1 ? index + 1 : ""} here...`}
                    value={textAnswers[index] || ""}
                    onChange={(e) => handleTextAnswerChange(index, e.target.value)}
                    disabled={hasAnswered}
                    className="w-full p-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !hasAnswered && textAnswers.every((answer) => answer.trim())) {
                        handleSubmitTextAnswers()
                      }
                    }}
                  />
                </div>
              ))}
              {!hasAnswered && (
                <Button
                  onClick={handleSubmitTextAnswers}
                  className="w-full bg-primary hover:bg-primary-600"
                  disabled={!textAnswers.every((answer) => answer.trim())}
                >
                  {getBlankCount(currentQuestion) > 1 ? t("quiz.submitAnswers") : t("quiz.submitAnswer")}
                </Button>
              )}
            </div>
          )}

          {hasAnswered && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div
                className={`font-semibold mb-2 ${areAnswersCorrect(selectedAnswer || [], currentQuestion.correctAnswers)
                    ? "text-green-700"
                    : "text-red-700"
                  }`}
              >
                {areAnswersCorrect(selectedAnswer || [], currentQuestion.correctAnswers)
                  ? t("quiz.correct")
                  : t("quiz.incorrect")}
              </div>

              {/* Show correct answer for both question types */}
              <div className="mb-2">
                <span className="font-medium text-blue-800">
                  {currentQuestion.correctAnswers.length > 1 ? t("quiz.correctAnswers") : t("quiz.correctAnswer")}:{" "}
                </span>
                <span className="text-blue-700">
                  {currentQuestion.type === "multiple-choice"
                    ? currentQuestion.correctAnswers.map((answerKey, index) => (
                      <span key={answerKey}>
                        {index > 0 && ", "}
                        {answerKey.toUpperCase()}.{" "}
                        <MathText inline>{currentQuestion.options?.[answerKey] || ""}</MathText>
                      </span>
                    ))
                    : currentQuestion.correctAnswers.map((answer, index) => (
                      <span key={index}>
                        {index > 0 && ", "}
                        <MathText inline>{answer}</MathText>
                      </span>
                    ))}
                </span>
              </div>

              {currentQuestion.explanation && (
                <div className="text-sm text-blue-600">
                  <MathText>{currentQuestion.explanation}</MathText>
                </div>
              )}
              <div className="mt-3">
                <Button onClick={onNext} className="w-full bg-primary hover:bg-primary-600">
                  {session.currentQuestionIndex === session.questions.length - 1
                    ? t("quiz.finishQuiz")
                    : t("quiz.nextQuestion")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
